import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { formatDateTime } from '../../utils/date';

const TitheReceiptModal = ({ receipt, onClose, onStatusUpdate }) => {
  const { t } = useTranslation();
  const [showRejectModal, setShowRejectModal] = useState(false);
  const [rejectRemarks, setRejectRemarks] = useState('');
  const [approveRemarks, setApproveRemarks] = useState('');
  const [showApproveModal, setShowApproveModal] = useState(false);

  // Format currency
  const formatCurrency = (amount) => {
    return `HK$${parseFloat(amount || 0).toFixed(2)}`;
  };

  // Format date
  // Handle approve with remarks
  const handleApprove = () => {
    onStatusUpdate(receipt.id, 'approved', approveRemarks);
    setShowApproveModal(false);
    onClose();
  };

  // Handle reject with remarks
  const handleReject = () => {
    onStatusUpdate(receipt.id, 'rejected', rejectRemarks);
    setShowRejectModal(false);
    onClose();
  };

  // Get status color
  const getStatusColor = (status) => {
    switch (status) {
      case 'pending': return 'text-yellow-600';
      case 'approved': return 'text-green-600';
      case 'rejected': return 'text-red-600';
      default: return 'text-gray-600';
    }
  };

  return (
    <>
      {/* Main Modal */}
      <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
        <div className="relative top-20 mx-auto p-5 border w-11/12 max-w-4xl shadow-lg rounded-md bg-white">
          {/* Header */}
          <div className="flex items-center justify-between mb-6">
            <h3 className="text-2xl font-bold text-gray-900">
              {t('titheReceipts.modal.title', { id: receipt.id })}
            </h3>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>

          {/* Status and Actions */}
          <div className="mb-6 flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <span className={`text-lg font-semibold ${getStatusColor(receipt.status)}`}>
                {t('titheReceipts.modal.status')}: {t(`titheReceipts.table.statusLabel.${receipt.status}`, receipt.status)}
              </span>
              <span className="text-sm text-gray-500">
                {t('titheReceipts.modal.created')} {formatDateTime(receipt.created_at)}
              </span>
            </div>
            
            {receipt.status === 'pending' && (
              <div className="flex space-x-3">
                <button
                  onClick={() => setShowApproveModal(true)}
                  className="bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded font-medium"
                >
                  {t('titheReceipts.modal.approve')}
                </button>
                <button
                  onClick={() => setShowRejectModal(true)}
                  className="bg-red-600 hover:bg-red-700 text-white px-4 py-2 rounded font-medium"
                >
                  {t('titheReceipts.modal.reject')}
                </button>
              </div>
            )}
          </div>

          {/* Content - List View by Sections */}
          <div className="space-y-6">
            {/* Personal Information */}
            <div className="border-b border-gray-200 pb-4">
              <h4 className="text-lg font-semibold text-gray-900 mb-3">{t('titheReceipts.modal.personalInfo')}</h4>
              <dl className="grid grid-cols-1 gap-x-4 gap-y-2 sm:grid-cols-2">
                <div>
                  <dt className="text-sm font-medium text-gray-600">{t('titheReceipts.modal.fields.chineseName')}</dt>
                  <dd className="text-sm text-gray-900">{receipt.chinese_name || t('common.na')}</dd>
                </div>
                <div>
                  <dt className="text-sm font-medium text-gray-600">{t('titheReceipts.modal.fields.englishName')}</dt>
                  <dd className="text-sm text-gray-900">{receipt.english_name || t('common.na')}</dd>
                </div>
                <div>
                  <dt className="text-sm font-medium text-gray-600">{t('titheReceipts.modal.fields.contactPhone')}</dt>
                  <dd className="text-sm text-gray-900">{receipt.contact_phone || t('common.na')}</dd>
                </div>
                <div>
                  <dt className="text-sm font-medium text-gray-600">{t('titheReceipts.modal.fields.email')}</dt>
                  <dd className="text-sm text-gray-900">{receipt.email || t('common.na')}</dd>
                </div>
                <div>
                  <dt className="text-sm font-medium text-gray-600">{t('titheReceipts.modal.fields.worshipCode')}</dt>
                  <dd className="text-sm text-gray-900">{receipt.worship_code || t('common.na')}</dd>
                </div>
              </dl>
            </div>

            {/* Address Information */}
            <div className="border-b border-gray-200 pb-4">
              <h4 className="text-lg font-semibold text-gray-900 mb-3">{t('titheReceipts.modal.addressInfo')}</h4>
              <dl className="grid grid-cols-1 gap-x-4 gap-y-2 sm:grid-cols-2">
                <div className="sm:col-span-2">
                  <dt className="text-sm font-medium text-gray-600">{t('titheReceipts.modal.fields.address')}</dt>
                  <dd className="text-sm text-gray-900">{receipt.address || t('common.na')}</dd>
                </div>
                <div>
                  <dt className="text-sm font-medium text-gray-600">{t('titheReceipts.modal.fields.district')}</dt>
                  <dd className="text-sm text-gray-900">{receipt.district || t('common.na')}</dd>
                </div>
                <div>
                  <dt className="text-sm font-medium text-gray-600">{t('titheReceipts.modal.fields.area')}</dt>
                  <dd className="text-sm text-gray-900">{receipt.area || t('common.na')}</dd>
                </div>
              </dl>
            </div>

            {/* Payment Information */}
            <div className="border-b border-gray-200 pb-4">
              <h4 className="text-lg font-semibold text-gray-900 mb-3">{t('titheReceipts.modal.paymentInfo')}</h4>
              <dl className="grid grid-cols-1 gap-x-4 gap-y-2 sm:grid-cols-2">
                <div>
                  <dt className="text-sm font-medium text-gray-600">{t('titheReceipts.modal.fields.receivingBank')}</dt>
                  <dd className="text-sm text-gray-900">{receipt.receiving_bank || t('common.na')}</dd>
                </div>
                <div>
                  <dt className="text-sm font-medium text-gray-600">{t('titheReceipts.modal.fields.transactionType')}</dt>
                  <dd className="text-sm text-gray-900">{receipt.transaction_type || t('common.na')}</dd>
                </div>
                <div>
                  <dt className="text-sm font-medium text-gray-600">{t('titheReceipts.modal.fields.depositDate')}</dt>
                  <dd className="text-sm text-gray-900">{formatDateTime(receipt.deposit_date)}</dd>
                </div>
                <div>
                  <dt className="text-sm font-medium text-gray-600">{t('titheReceipts.modal.fields.referenceNumber')}</dt>
                  <dd className="text-sm text-gray-900">{receipt.reference_number || t('common.na')}</dd>
                </div>
              </dl>
            </div>

            {/* Donation Breakdown */}
                        {/* Donation Breakdown */}
            <div className="bg-green-50 p-4 rounded-lg">
              <h4 className="text-lg font-semibold text-gray-900 mb-3">{t('titheReceipts.modal.donationBreakdown')}</h4>
              <div className="space-y-2">
                <div className="flex justify-between">
                  <span className="text-sm font-medium text-gray-600">{t('titheReceipts.modal.fields.tithe')}</span>
                  <span className="text-sm text-gray-900 font-medium">{formatCurrency(receipt.tithe_amount)}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-sm font-medium text-gray-600">{t('titheReceipts.modal.fields.thanksgiving')}</span>
                  <span className="text-sm text-gray-900 font-medium">{formatCurrency(receipt.thanksgiving_offering)}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-sm font-medium text-gray-600">{t('titheReceipts.modal.fields.churchOperating')}</span>
                  <span className="text-sm text-gray-900 font-medium">{formatCurrency(receipt.church_operating)}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-sm font-medium text-gray-600">{t('titheReceipts.modal.fields.buildingFund')}</span>
                  <span className="text-sm text-gray-900 font-medium">{formatCurrency(receipt.building_fund)}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-sm font-medium text-gray-600">{t('titheReceipts.modal.fields.gospelMinistry')}</span>
                  <span className="text-sm text-gray-900 font-medium">{formatCurrency(receipt.gospel_ministry)}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-sm font-medium text-gray-600">{t('titheReceipts.modal.fields.loveNeighbor')}</span>
                  <span className="text-sm text-gray-900 font-medium">{formatCurrency(receipt.love_neighbor_fund)}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-sm font-medium text-gray-600">{t('titheReceipts.modal.fields.specialOffering')}</span>
                  <span className="text-sm text-gray-900 font-medium">{formatCurrency(receipt.special_offering)}</span>
                </div>
                {receipt.special_offering_description && (
                  <div className="mt-2">
                    <span className="text-sm font-medium text-gray-600">{t('titheReceipts.modal.fields.specialOfferingDesc')}</span>
                    <p className="text-sm text-gray-900 bg-white p-2 rounded border mt-1">{receipt.special_offering_description}</p>
                  </div>
                )}
                <div className="flex justify-between">
                  <span className="text-sm font-medium text-gray-600">{t('titheReceipts.modal.fields.otherOffering')}</span>
                  <span className="text-sm text-gray-900 font-medium">{formatCurrency(receipt.other_offering)}</span>
                </div>
                {receipt.other_offering_description && (
                  <div className="mt-2">
                    <span className="text-sm font-medium text-gray-600">{t('titheReceipts.modal.fields.otherOfferingDesc')}</span>
                    <p className="text-sm text-gray-900 bg-white p-2 rounded border mt-1">{receipt.other_offering_description}</p>
                  </div>
                )}
                <hr className="my-2" />
                <div className="flex justify-between text-lg font-bold">
                  <span className="text-gray-900">{t('titheReceipts.modal.fields.totalAmount')}</span>
                  <span className="text-gray-900">{formatCurrency(receipt.total_amount)}</span>
                </div>
              </div>
            </div>

            {/* Additional Information */}
            <div>
              <h4 className="text-lg font-semibold text-gray-900 mb-3">{t('titheReceipts.modal.additionalInfo')}</h4>
              <dl className="grid grid-cols-1 gap-x-4 gap-y-2 sm:grid-cols-2">
                <div>
                  <dt className="text-sm font-medium text-gray-600">{t('titheReceipts.modal.fields.receiptRequired')}</dt>
                  <dd className="text-sm text-gray-900">{receipt.receipt_required ? t('titheReceipts.modal.fields.yes') : t('titheReceipts.modal.fields.no')}</dd>
                </div>
                <div>
                  <dt className="text-sm font-medium text-gray-600">{t('titheReceipts.modal.fields.receiptType')}</dt>
                  <dd className="text-sm text-gray-900">
                    {receipt.receipt_frequency === 'yearly'
                      ? t('titheReceipts.modal.fields.receiptYearly')
                      : t('titheReceipts.modal.fields.receiptIndependent')}
                  </dd>
                </div>
                <div>
                  <dt className="text-sm font-medium text-gray-600">{t('titheReceipts.modal.fields.languagePreference')}</dt>
                  <dd className="text-sm text-gray-900">{receipt.receipt_language || t('common.na')}</dd>
                </div>
                {receipt.receipt_file_name && (
                  <div>
                    <dt className="text-sm font-medium text-gray-600">{t('titheReceipts.modal.fields.uploadedReceipt')}</dt>
                    <dd className="text-sm text-blue-600 underline cursor-pointer">{receipt.receipt_file_name}</dd>
                  </div>
                )}
                {receipt.remarks && (
                  <div className="sm:col-span-2">
                    <dt className="text-sm font-medium text-gray-600">{t('titheReceipts.modal.fields.remarks')}</dt>
                    <dd className="text-sm text-gray-900 mt-1 p-2 bg-gray-50 rounded border">{receipt.remarks}</dd>
                  </div>
                )}
                {receipt.processed_by_admin_id && (
                  <div>
                    <dt className="text-sm font-medium text-gray-600">{t('titheReceipts.modal.fields.processedBy')}</dt>
                    <dd className="text-sm text-gray-900">Admin #{receipt.processed_by_admin_id}</dd>
                  </div>
                )}
                {receipt.processed_at && (
                  <div>
                    <dt className="text-sm font-medium text-gray-600">{t('titheReceipts.modal.fields.processedAt')}</dt>
                    <dd className="text-sm text-gray-900">{formatDateTime(receipt.processed_at)}</dd>
                  </div>
                )}
              </dl>
            </div>
          </div>

          {/* Footer */}
          <div className="mt-6 flex justify-end">
            <button
              onClick={onClose}
              className="bg-gray-500 hover:bg-gray-700 text-white font-bold py-2 px-4 rounded"
            >
              {t('common.close')}
            </button>
          </div>
        </div>
      </div>

      {/* Approve Modal */}
      {showApproveModal && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-60">
          <div className="relative top-20 mx-auto p-5 border w-96 shadow-lg rounded-md bg-white">
            <h3 className="text-lg font-bold text-gray-900 mb-4">{t('titheReceipts.modal.approve')}</h3>
            <p className="text-sm text-gray-600 mb-4">
              {t('titheReceipts.modal.approveConfirm')}
            </p>
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                {t('titheReceipts.modal.remarks')}
              </label>
              <textarea
                value={approveRemarks}
                onChange={(e) => setApproveRemarks(e.target.value)}
                rows={3}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500"
                placeholder=""
              />
            </div>
            <div className="flex justify-end space-x-3">
              <button
                onClick={() => setShowApproveModal(false)}
                className="bg-gray-500 hover:bg-gray-700 text-white font-bold py-2 px-4 rounded"
              >
                {t('common.cancel')}
              </button>
              <button
                onClick={handleApprove}
                className="bg-green-600 hover:bg-green-700 text-white font-bold py-2 px-4 rounded"
              >
                {t('titheReceipts.modal.approveSubmit')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Reject Modal */}
      {showRejectModal && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-60">
          <div className="relative top-20 mx-auto p-5 border w-96 shadow-lg rounded-md bg-white">
            <h3 className="text-lg font-bold text-gray-900 mb-4">{t('titheReceipts.modal.reject')}</h3>
            <p className="text-sm text-gray-600 mb-4">
              {t('titheReceipts.modal.rejectConfirm')}
            </p>
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                {t('titheReceipts.modal.remarks')}
              </label>
              <textarea
                value={rejectRemarks}
                onChange={(e) => setRejectRemarks(e.target.value)}
                rows={4}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-red-500"
                placeholder=""
                required
              />
            </div>
            <div className="flex justify-end space-x-3">
              <button
                onClick={() => setShowRejectModal(false)}
                className="bg-gray-500 hover:bg-gray-700 text-white font-bold py-2 px-4 rounded"
              >
                {t('common.cancel')}
              </button>
              <button
                onClick={handleReject}
                disabled={!rejectRemarks.trim()}
                className="bg-red-600 hover:bg-red-700 text-white font-bold py-2 px-4 rounded disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {t('titheReceipts.modal.rejectSubmit')}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default TitheReceiptModal;
